import { createContext, useContext, useMemo, useState, type ReactNode } from 'react'

export type PeriodKey =
  | 'today' | 'this_week' | 'last_2_weeks'
  | '7d' | '30d'
  | 'this_month' | 'last_month' | 'this_quarter' | 'ytd' | 'last_year' | 'this_year'
  | 'custom'

export interface Period {
  key: PeriodKey
  label: string
  labelKey: string
  from: string   // YYYY-MM-DD
  to: string     // YYYY-MM-DD (exclusive upper bound recommended for ranges)
}

function startOfMonth(d: Date): Date { return new Date(d.getFullYear(), d.getMonth(), 1) }
function startOfQuarter(d: Date): Date { return new Date(d.getFullYear(), Math.floor(d.getMonth() / 3) * 3, 1) }
function startOfWeek(d: Date): Date {
  // Monday as week start
  const day = d.getDay() // 0=Sun, 1=Mon, ...
  const diff = day === 0 ? 6 : day - 1
  const r = new Date(d.getFullYear(), d.getMonth(), d.getDate() - diff)
  return r
}
function iso(d: Date): string { return d.toISOString().slice(0, 10) }

export function computePeriod(key: PeriodKey, customFrom?: string, customTo?: string): Period {
  const now = new Date()
  const today = iso(now)
  switch (key) {
    case 'today':
      return { key, label: 'Today', labelKey: 'period.today', from: today, to: today }
    case 'this_week':
      return { key, label: 'This week', labelKey: 'period.this_week', from: iso(startOfWeek(now)), to: today }
    case 'last_2_weeks': {
      const from = new Date(now); from.setDate(from.getDate() - 14)
      return { key, label: 'Last 2 weeks', labelKey: 'period.last_2_weeks', from: iso(from), to: today }
    }
    case '7d': {
      const from = new Date(now); from.setDate(from.getDate() - 7)
      return { key, label: 'Last 7 days', labelKey: 'period.this_week', from: iso(from), to: today }
    }
    case '30d': {
      const from = new Date(now); from.setDate(from.getDate() - 30)
      return { key, label: 'Last 30 days', labelKey: 'period.this_month', from: iso(from), to: today }
    }
    case 'this_month':
      return { key, label: 'This month', labelKey: 'period.this_month', from: iso(startOfMonth(now)), to: today }
    case 'last_month': {
      const start = new Date(now.getFullYear(), now.getMonth() - 1, 1)
      const end   = new Date(now.getFullYear(), now.getMonth(),     1) // exclusive
      return { key, label: 'Last month', labelKey: 'period.this_month', from: iso(start), to: iso(end) }
    }
    case 'this_quarter':
      return { key, label: 'This quarter', labelKey: 'period.this_quarter', from: iso(startOfQuarter(now)), to: today }
    case 'ytd':
    case 'this_year':
      return { key, label: `This year (${now.getFullYear()})`, labelKey: 'period.this_year', from: iso(new Date(now.getFullYear(), 0, 1)), to: today }
    case 'last_year': {
      const start = new Date(now.getFullYear() - 1, 0, 1)
      const end   = new Date(now.getFullYear(), 0, 1) // exclusive
      return { key, label: String(now.getFullYear() - 1), labelKey: 'period.this_year', from: iso(start), to: iso(end) }
    }
    case 'custom':
      return { key, label: 'Custom period', labelKey: 'period.custom', from: customFrom ?? today, to: customTo ?? today }
  }
}

/** Standardized list of period options for chart-level pickers (matches the wireframe). */
export const STANDARD_PERIODS: { key: PeriodKey; label: string; labelKey: string }[] = [
  { key: 'today',        label: 'Today',          labelKey: 'period.today' },
  { key: 'this_week',    label: 'This week',      labelKey: 'period.this_week' },
  { key: 'last_2_weeks', label: 'Last 2 weeks',   labelKey: 'period.last_2_weeks' },
  { key: 'this_month',   label: 'This month',     labelKey: 'period.this_month' },
  { key: 'this_quarter', label: 'This quarter',   labelKey: 'period.this_quarter' },
  { key: 'this_year',    label: 'This year',      labelKey: 'period.this_year' },
  { key: 'custom',       label: 'Custom period…', labelKey: 'period.custom' },
]

interface Ctx {
  period: Period
  setPeriodKey: (k: PeriodKey) => void
  setCustom: (from: string, to: string) => void
}

const PeriodContext = createContext<Ctx>({
  period: computePeriod('ytd'),
  setPeriodKey: () => {},
  setCustom: () => {},
})

export function PeriodProvider({ children }: { children: ReactNode }) {
  const [key, setKey] = useState<PeriodKey>('ytd')
  const [custom, setCustomState] = useState<{ from: string; to: string } | null>(null)
  const period = useMemo(() => computePeriod(key, custom?.from, custom?.to), [key, custom])
  return (
    <PeriodContext.Provider value={{
      period,
      setPeriodKey: setKey,
      setCustom: (from, to) => { setCustomState({ from, to }); setKey('custom') },
    }}>
      {children}
    </PeriodContext.Provider>
  )
}

export const usePeriod = () => useContext(PeriodContext)
